import React from "react";
import { ArrowRight, Play, Users } from "lucide-react";
import { AppLink } from "./AppLink";

const HEADING = { fontFamily: "'Space Grotesk', system-ui, sans-serif" } as const;

export function HeroSection() {
  return (
    <section className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #4f46e5 1px, transparent 1px), linear-gradient(to bottom, #4f46e5 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at 50% 20%, #000 40%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 20%, #000 40%, transparent 80%)",
        }}
      />
      {/* Glow orbs */}
      <div className="absolute -top-24 left-1/4 w-[500px] h-[500px] bg-[#4f46e5]/20 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[420px] h-[420px] bg-[#1e1e5a]/60 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Headline card */}
          <div className="lg:col-span-8 bg-[#141432] rounded-[2rem] p-8 md:p-14 flex flex-col justify-center border border-[#1e1e5a] relative overflow-hidden group">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#4f46e5] opacity-10 blur-[100px] rounded-full pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1e1e5a] text-[#a5b4fc] text-xs font-bold uppercase tracking-widest mb-6 border border-[#4f46e5]/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4f46e5] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4f46e5]" />
                </span>
                Nouvelle génération de gestion de projet
              </span>
              <h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] mb-8 tracking-tighter text-white"
                style={HEADING}
              >
                Unifiez vos{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4f46e5] to-[#a5b4fc]">
                  flux de travail
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-400 max-w-xl leading-relaxed mb-10">
                Lapyno centralise tâches, charge d'équipe, temps et budgets dans un workspace unique
                — pensé pour les équipes qui veulent vraiment avancer.
              </p>
              <div className="flex flex-wrap gap-4">
                <AppLink
                  href="https://app.lapyno.com/?mode=signup"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#4f46e5] hover:bg-[#4338ca] text-white rounded-2xl font-bold text-base transition-all shadow-[0_10px_40px_-10px_rgba(79,70,229,0.7)] hover:scale-[1.02]"
                >
                  Essai gratuit 14 jours
                  <ArrowRight className="h-4 w-4" />
                </AppLink>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#1e1e5a]/60 border border-white/10 text-white rounded-2xl font-bold text-base hover:bg-[#1e1e5a] transition-colors"
                >
                  <Play className="h-4 w-4" />
                  Voir la démo
                </button>
              </div>
            </div>
          </div>

          {/* Team load bento */}
          <div className="lg:col-span-4 bg-[#141432] rounded-[2rem] p-8 border border-[#1e1e5a] flex flex-col">
            <div className="flex justify-between items-center mb-8">
              <h3 className="font-bold text-xl text-white" style={HEADING}>
                Charge équipe
              </h3>
              <div className="w-10 h-10 rounded-full bg-[#1e1e5a] flex items-center justify-center">
                <Users className="h-5 w-5 text-[#a5b4fc]" />
              </div>
            </div>
            <div className="space-y-4">
              {[
                { grad: "from-indigo-500 to-purple-500", pct: 85 },
                { grad: "from-emerald-500 to-teal-500", pct: 40 },
                { grad: "from-amber-500 to-orange-500", pct: 65 },
              ].map((row, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-tr ${row.grad}`} />
                  <div className="flex-1">
                    <div className="h-2 bg-[#1e1e5a] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#4f46e5] rounded-full shadow-[0_0_10px_#4f46e5]"
                        style={{ width: `${row.pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs font-bold text-white w-10 text-right">{row.pct}%</span>
                </div>
              ))}
            </div>
            <div className="mt-auto pt-6 text-sm text-slate-400 italic">
              Optimisation recommandée : +12% de capacité
            </div>
          </div>

          {/* Kanban bento */}
          <div className="lg:col-span-4 bg-[#141432] rounded-[2rem] p-8 border border-[#1e1e5a] relative overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-xl text-white" style={HEADING}>
                Tâches & Kanban
              </h3>
              <span className="text-[10px] uppercase tracking-widest text-[#a5b4fc] font-bold">
                Live
              </span>
            </div>
            <div className="flex gap-3">
              <div className="flex-1 bg-[#1e1e5a]/60 rounded-xl p-3 space-y-2">
                <div className="h-12 bg-[#0a0a1a] rounded-lg border border-white/5" />
                <div className="h-12 bg-[#4f46e5]/20 border border-[#4f46e5]/40 rounded-lg" />
              </div>
              <div className="flex-1 bg-[#1e1e5a]/40 rounded-xl p-3 space-y-2 opacity-60">
                <div className="h-12 bg-[#0a0a1a] rounded-lg" />
              </div>
              <div className="flex-1 bg-[#1e1e5a]/40 rounded-xl p-3 space-y-2 opacity-40">
                <div className="h-12 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" />
              </div>
            </div>
            <p className="mt-6 text-sm text-slate-400">
              Kanban, liste, calendrier, Gantt — sans changer d'onglet.
            </p>
          </div>

          {/* Budget bento */}
          <div className="lg:col-span-8 bg-gradient-to-br from-[#141432] to-[#1e1e5a] rounded-[2rem] p-8 md:p-10 border border-[#4f46e5]/30 relative group overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#4f46e5]/20 blur-[100px] rounded-full pointer-events-none" />
            <div className="relative flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1">
                <span className="text-[10px] uppercase tracking-widest text-[#a5b4fc] font-bold">
                  Budget projet
                </span>
                <h3 className="font-bold text-2xl md:text-3xl mt-2 mb-4 text-white" style={HEADING}>
                  Suivi budgétaire précis
                </h3>
                <p className="text-slate-300 mb-6 max-w-md">
                  Ne dépassez plus jamais vos prévisions. Analyse en temps réel du coût main d'œuvre
                  vs budget projet.
                </p>
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-white tabular-nums" style={HEADING}>
                    84 200 €
                  </span>
                  <span className="text-emerald-400 text-sm font-bold">+4.2% ce mois</span>
                </div>
              </div>
              <div className="w-full md:w-64 h-40 bg-[#0a0a1a] rounded-2xl border border-white/5 flex items-end p-4 gap-2">
                {[60, 40, 75, 50, 90].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 bg-[#4f46e5] rounded-t-lg transition-all duration-500 shadow-[0_0_16px_rgba(79,70,229,0.4)]"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
